import * as React from 'react';
import {useContext} from 'react';
import {alpha, styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {OrangeTrieLogoButton} from './OrangeTrieLogoButton';
import {NavLink, useLocation, useNavigate} from 'react-router';
import {AuthContext} from "../components/AuthContext";
import MenuItem from "@mui/material/MenuItem";
import {SubscriptionTier} from "../utils/types";

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
}));

export default function AppAppBar() {
  const [open, setOpen] = React.useState(false);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate()
  const location = useLocation();
  const currentSubscriptionTier = authContext.getSubscriptionTier();
  const isSelectedPath = (path: string): boolean => {
    return location.pathname === path;
  }

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const handleClickLibrary = () => {
    navigate('/');
  }

  const handleClickBacktests = () => {
    navigate('/backtests')
  }

  const handleClickFeedback = () => {
    navigate('/feedback')
  }

  const handleClickAppIcon = () => {
    navigate('/');
  }

  const getSubscriptionButtonText = (): string => {
    if (currentSubscriptionTier === SubscriptionTier.free ||
        currentSubscriptionTier === SubscriptionTier.plus) {
      return "Upgrade"
    }
    return "Subscription"
  }

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth={false}>
        <StyledToolbar variant="dense" disableGutters>

          <Box sx={{flexGrow: 1, display: 'flex', alignItems: 'center', px: 0}}>

            {/* TOP BAR DESKTOP VIEW COMPONENTS - LEFT SIDE */}

            <OrangeTrieLogoButton
              onClick={handleClickAppIcon}
            />

            {authContext.isAuthenticated() ? (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
                <Button
                    onClick={handleClickLibrary}
                    color="primary"
                    variant={isSelectedPath('/') ? "outlined" : "text"}
                    size="medium"
                    fullWidth
                >
                  Strategies
                </Button>
                {authContext.isAuthenticated() && authContext.getSubscriptionTier() !== SubscriptionTier.free && (
                  <Button
                      onClick={handleClickBacktests}
                      color="primary"
                      // variant="outlined"
                      variant={isSelectedPath('/backtests') ? "outlined" : "text"}
                      size="medium"
                      fullWidth
                  >
                    Backtests
                  </Button>
                    )}
              </Box>
            ) : (
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button
                  variant="text"
                  color="info"
                  size="small"
                  onClick={() => {
                    const pricingSection = document.getElementById('pricing');
                    if (pricingSection) {
                      pricingSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  Pricing
                </Button>
              </Box>
            )}
          </Box>


          {/* TOP BAR DESKTOP VIEW COMPONENTS - RIGHT SIDE */}
          <Box
              sx={{
                display: {md: 'flex', xs: 'none'}, // xs: 'none' hides this in mobile view
                gap: 2
              }}
          >
            {authContext.isAuthenticated() && (
            <Button
                component={NavLink}
                to={"/subscribe"}
                color="primary"
                variant={isSelectedPath('/subscribe') ? "outlined" : "text"}
                size="medium"
                fullWidth
            >
              {getSubscriptionButtonText()}
            </Button>
            )}
            {authContext.isAuthenticated() && (
            <Button
                component={NavLink}
                to={"/docs"}
                color="primary"
                variant={isSelectedPath('/docs') ? "outlined" : "text"}
                size="medium"
                fullWidth
            >
              API Docs
            </Button>
            )}
            {authContext.isAuthenticated() && (
                <Button
                    component={NavLink}
                    to={"/account"}
                    color="primary"
                    variant={isSelectedPath('/account') ? "outlined" : "text"}
                    size="medium"
                    fullWidth
                >
                  Account
                </Button>
            )}
            {authContext.isAuthenticated() && (
                <Button
                    component={NavLink}
                    to={"/feedback"}
                    color="primary"
                    variant={isSelectedPath('/feedback') ? "outlined" : "text"}
                    size="medium"
                    fullWidth
                >
                  Feedback
                </Button>
            )}
            {!authContext.isAuthenticated() && (
                <></>
            )}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex', minWidth: 80},
              gap: 1,
              alignItems: 'center',
            }}
          >
            {authContext.isAuthenticated() ? (
            <Button
              component={NavLink}
              to={"/login"}
              color="primary"
              variant="contained"
              size="small"
              onClick={() => {
                authContext.logout()
              }}
            >
              Log out
            </Button>
            ) : (
            <Button fullWidth component={NavLink} to={"/login"} color="primary" variant="contained" size="small">
              Sign in
            </Button>
            )}
          </Box>
          </Box>

          {/* TOP BAR MOBILE VIEW COMPONENTS */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  top: 'var(--template-frame-height, 0px)',
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* TOP BAR MOBILE VIEW COMPONENTS (LOGGED OUT) */}
                {!authContext.isAuthenticated() && (
                  <MenuItem>
                    <Button
                      variant="text"
                      color="info"
                      size="small"
                      fullWidth
                      onClick={() => {
                      const pricingSection = document.getElementById('pricing');
                      if (pricingSection) {
                      pricingSection.scrollIntoView({ behavior: 'smooth' });
                    }
                      setOpen(false);
                    }}>
                    Pricing
                    </Button>
                  </MenuItem>
                )}

                {/* TOP BAR MOBILE VIEW COMPONENTS (LOGGED IN) */}
                {authContext.isAuthenticated() && (
                <MenuItem>
                  <Button
                      onClick={handleClickLibrary}
                      color="primary"
                      variant={isSelectedPath('/') ? "outlined" : "text"}
                      size="small"
                      fullWidth
                  >
                    Strategies
                  </Button>
                </MenuItem>
                )}

                {authContext.isAuthenticated() && authContext.getSubscriptionTier() !== SubscriptionTier.free && (
                  <MenuItem>
                    <Button
                        onClick={handleClickBacktests}
                        color="primary"
                        variant={isSelectedPath('/backtests') ? "outlined" : "text"}
                        size="medium"
                        fullWidth
                    >
                      Backtests
                    </Button>
                  </MenuItem>
                  )}
                {authContext.isAuthenticated() && (
                <MenuItem>
                  <Button
                      component={NavLink}
                      to={"/subscribe"}
                      color="primary"
                      variant={isSelectedPath('/subscribe') ? "outlined" : "text"}
                      size="small"
                      fullWidth
                  >
                    {getSubscriptionButtonText()}
                  </Button>
                </MenuItem>
                )}

                {authContext.isAuthenticated() && authContext.getSubscriptionTier() === SubscriptionTier.free && (
                    <MenuItem>
                      <Button
                          component={NavLink}
                          to={"/subscribe"}
                          color="primary"
                          variant={isSelectedPath('/subscribe') ? "outlined" : "text"}
                          size="small"
                          fullWidth
                      >
                        {getSubscriptionButtonText()}
                      </Button>
                    </MenuItem>
                )}
                {authContext.isAuthenticated() && (
                    <MenuItem>
                      <Button
                          component={NavLink}
                          to={"/docs"}
                          color="primary"
                          variant={isSelectedPath('/docs') ? "outlined" : "text"}
                          size="small"
                          fullWidth
                          onClick={() => setOpen(false)}
                      >
                        API Docs
                      </Button>
                    </MenuItem>
                )}
                {authContext.isAuthenticated() && (
                    <MenuItem>
                      <Button
                          component={NavLink}
                          to={"/account"}
                          color="primary"
                          variant={isSelectedPath('/account') ? "outlined" : "text"}
                          size="small"
                          fullWidth
                      >
                        Account
                      </Button>
                    </MenuItem>
                )}
                {authContext.isAuthenticated() && (
                    <MenuItem>
                      <Button
                          onClick={handleClickFeedback}
                          color="primary"
                          variant={isSelectedPath('/feedback') ? "outlined" : "text"}
                          size="small"
                          fullWidth
                      >
                        Feedback
                      </Button>
                    </MenuItem>
                )}
                <MenuItem>
                  {authContext.isAuthenticated() ? (
                      <Button
                          component={NavLink}
                          to={"/login"}
                          color="primary"
                          variant="contained"
                          size="small"
                          onClick={() => {
                            authContext.logout()
                          }}
                          fullWidth
                      >
                        Log out
                      </Button>
                  ) : (
                      <Button fullWidth component={NavLink} to={"/login"} color="primary" variant="outlined" size="small">
                        Sign in
                      </Button>
                  )}
                </MenuItem>
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}